/**
 * Image Cache Service using Cache API
 * 
 * Uses browser's Cache API for efficient image caching.
 * Can store GBs of images without quota issues.
 * 
 * Cache Duration: 30 seconds - ensures fresh images while reducing server load
 * Cache Invalidation: Handled by Supabase storage timestamps in URLs
 */

const CACHE_NAME = 'cosmo-product-images-v1';
const CACHE_MAX_AGE = 30 * 1000; // 30 seconds in milliseconds

class ImageCacheService {
    private cache: Cache | null = null;

    async getCache(): Promise<Cache> {
        if (!this.cache) {
            this.cache = await caches.open(CACHE_NAME);
        }
        return this.cache;
    }

    /**
     * Get cached image or fetch and cache it
     * Checks cache age and refetches if older than CACHE_MAX_AGE
     */
    async getCachedImage(url: string): Promise<string> {
        try {
            const cache = await this.getCache();
            
            // Check if image is in cache
            const cachedResponse = await cache.match(url);
            
            if (cachedResponse) {
                // Check cache age from Date header
                const dateHeader = cachedResponse.headers.get('date');
                if (dateHeader) {
                    const cachedTime = new Date(dateHeader).getTime();
                    const now = Date.now();
                    const age = now - cachedTime;
                    
                    // If cache is older than MAX_AGE, refetch
                    if (age > CACHE_MAX_AGE) {
                        await cache.delete(url);
                    } else {
                        // Cache is still valid - return it
                        const blob = await cachedResponse.blob();
                        return URL.createObjectURL(blob);
                    }
                } else {
                    // No date header, return cached version anyway
                    const blob = await cachedResponse.blob();
                    return URL.createObjectURL(blob);
                }
            }
            
            // Not in cache or expired - fetch and store
            const response = await fetch(url);
            
            if (response.ok) {
                // Clone response before caching (can only read once)
                const responseClone = response.clone();
                await cache.put(url, responseClone);
                
                const blob = await response.blob();
                return URL.createObjectURL(blob);
            }
            
            // Fetch failed - return original URL
            return url;
        } catch (error) {
            console.warn('⚠️ [ImageCache] Failed to cache:', url, error);
            return url;
        }
    }

    /**
     * Preload multiple images
     */
    async preloadImages(urls: string[]): Promise<void> {
        try {
            const cache = await this.getCache();
            await cache.addAll(urls);
        } catch (error) {
            console.warn('⚠️ [ImageCache] Preload failed:', error);
        }
    }

    /**
     * Clear all cached images
     */
    async clearCache(): Promise<void> {
        await caches.delete(CACHE_NAME);
        this.cache = null;
    }

    /**
     * Get cache size (approximate)
     */
    async getCacheSize(): Promise<number> {
        try {
            const cache = await this.getCache();
            const keys = await cache.keys();
            return keys.length;
        } catch {
            return 0;
        }
    }

    /**
     * Force refresh a specific image (bypasses cache)
     */
    async refreshImage(url: string): Promise<string> {
        try {
            const cache = await this.getCache();
            
            // Delete from cache if exists
            await cache.delete(url);
            
            // Fetch fresh version
            const response = await fetch(url, { cache: 'reload' });
            
            if (response.ok) {
                // Cache the new version
                const responseClone = response.clone();
                await cache.put(url, responseClone);
                
                const blob = await response.blob();
                return URL.createObjectURL(blob);
            }
            
            return url;
        } catch (error) {
            console.warn('⚠️ [ImageCache] Failed to refresh:', url, error);
            return url;
        }
    }
}

export const imageCacheService = new ImageCacheService();

